import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, LayoutGrid, GitCompareArrows } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import { galleryImages, HERO_BACKGROUNDS } from "@/data/projectImages";
import { beforeAfterProjects, fertigeProjekte } from "@/data/projectData";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import PageHero from "@/components/PageHero";

const Gallery = () => {
  const { t, lang } = useT();
  const [mode, setMode] = useState("vorher-nachher"); // Default: Vorher & Nachher
  const [filter, setFilter] = useState("all");

  const filters = [
    { key: "all", label: t.gallery.filter_all },
    { key: "innen", label: t.gallery.filter_innen },
    { key: "aussen", label: t.gallery.filter_aussen },
    { key: "holz", label: t.gallery.filter_holz },
    { key: "spezial", label: t.gallery.filter_spezial },
  ];

  // Merge legacy gallery + new fertige projekte, unique
  const allFertige = [...fertigeProjekte, ...galleryImages];
  const filteredFertige = filter === "all" ? allFertige : allFertige.filter((i) => i.category === filter);
  const filteredBA = filter === "all" ? beforeAfterProjects : beforeAfterProjects.filter((i) => i.category === filter);

  const modeLabels = {
    vorher_nachher: { de: "Vorher & Nachher", en: "Before & After", fr: "Avant & Après", it: "Prima & Dopo" },
    fertige: { de: "Fertige Projekte", en: "Finished Projects", fr: "Projets terminés", it: "Progetti finiti" },
  };

  return (
    <div data-testid="gallery-page">
      <PageHero
        overline={t.gallery.overline}
        title={t.gallery.title}
        subtitle={t.gallery.subtitle}
        backgroundImage={HERO_BACKGROUNDS.gallery}
      />

      <section className="py-10 sm:py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mode toggle - Pill like segmented control in header */}
          <div className="flex justify-center mb-6 sm:mb-8" data-testid="gallery-mode-toggle">
            <div className="inline-flex items-center bg-zinc-100 p-1 rounded-full relative">
              {[
                { value: "fertige", label: modeLabels.fertige[lang], icon: LayoutGrid },
                { value: "vorher-nachher", label: modeLabels.vorher_nachher[lang], icon: GitCompareArrows },
              ].map((m) => {
                const active = mode === m.value;
                const Icon = m.icon;
                return (
                  <button
                    key={m.value}
                    onClick={() => setMode(m.value)}
                    className={`relative flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold rounded-full transition-colors ${
                      active ? "text-white" : "text-zinc-600 hover:text-zinc-900"
                    }`}
                    data-testid={`mode-${m.value}`}
                  >
                    {active && (
                      <motion.div
                        layoutId="galleryModePill"
                        className="absolute inset-0 bg-red-600 rounded-full"
                        transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                      />
                    )}
                    <Icon size={16} className="relative z-10" strokeWidth={2.2} />
                    <span className="relative z-10">{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Category filter pills (works with both modes) */}
          <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 mb-8 sm:mb-12" data-testid="gallery-filters">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-bold border transition-all ${
                  filter === f.key
                    ? "border-red-600 bg-red-600 text-white"
                    : "border-zinc-200 text-zinc-700 hover:border-zinc-900 hover:text-zinc-900"
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Content: Vorher/Nachher OR Fertige Projekte */}
          <AnimatePresence mode="wait">
            {mode === "vorher-nachher" ? (
              <motion.div
                key="ba"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {filteredBA.length === 0 ? (
                  <p className="text-center text-zinc-500 py-20">Keine Vorher/Nachher Projekte in dieser Kategorie.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-testid="ba-grid">
                    {filteredBA.map((ba) => (
                      <BeforeAfterSlider
                        key={ba.id}
                        before={ba.before}
                        after={ba.after}
                        title={ba.title[lang]}
                        description={ba.description[lang]}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="fertige"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {filteredFertige.length === 0 ? (
                  <p className="text-center text-zinc-500 py-20">Keine fertigen Projekte in dieser Kategorie.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
                    {filteredFertige.map((img) => (
                      <motion.div
                        key={img.id}
                        layout
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.35 }}
                        className="aspect-square overflow-hidden bg-zinc-100 group relative"
                        data-testid={`grid-${img.id}`}
                      >
                        <img src={img.url} alt={img.title} className="w-full h-full object-cover img-zoom" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                          <span className="text-xs font-semibold text-white">{img.title}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold tracking-tighter text-zinc-900">
            {t.home.cta_section_title}
          </h2>
          <p className="mt-5 text-base text-zinc-600 leading-relaxed max-w-2xl mx-auto">{t.home.cta_section_text}</p>
          <div className="mt-8">
            <Link to="/kontakt" className="btn-primary btn-glow" data-testid="gallery-cta">
              {t.common.cta_offer} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
